import BaseController from "confinus_ui/base_controller";

/**
 * Stimulus controller for toggling between raw and smoothed metric chart data
 *
 * This controller manages a toggle switch that allows users to switch between
 * viewing raw daily metric values and smoothed (moving average) values in charts.
 *
 * Data attributes required:
 * - data-metrics-ui--chart-smoothing-toggle-metric-name-value: The metric name
 * - data-metrics-ui--chart-smoothing-toggle-raw-data-value: JSON array of raw data points
 * - data-metrics-ui--chart-smoothing-toggle-smoothed-data-value: JSON array of smoothed data points
 * - data-metrics-ui--chart-smoothing-toggle-default-view-value: "raw" or "smoothed"
 *
 * Targets:
 * - chart: The chart element to update (must have a Chartkick instance)
 * - toggle: The toggle button element
 *
 * @example
 *   <div data-controller="metrics-ui--chart-smoothing-toggle"
 *        data-metrics-ui--chart-smoothing-toggle-metric-name-value="cart_abandonment_rate"
 *        data-metrics-ui--chart-smoothing-toggle-raw-data-value="<%= @raw_data.to_json %>"
 *        data-metrics-ui--chart-smoothing-toggle-smoothed-data-value="<%= @smoothed_data.to_json %>"
 *        data-metrics-ui--chart-smoothing-toggle-default-view-value="smoothed">
 *     <button data-metrics-ui--chart-smoothing-toggle-target="toggle"
 *             data-action="click->metrics-ui--chart-smoothing-toggle#toggle">
 *       Toggle View
 *     </button>
 *     <div data-metrics-ui--chart-smoothing-toggle-target="chart" id="chart_cart_abandonment_rate">
 *       <!-- Chart rendered here -->
 *     </div>
 *   </div>
 */
export default class ChartSmoothingToggleController extends BaseController {
  static values = {
    metricName: String,
    rawData: String, // JSON string of raw data points
    smoothedData: String, // JSON string of smoothed data points
    defaultView: String, // "raw" or "smoothed"
  };

  static targets = ["chart", "toggle", "rawLabel", "smoothedLabel"];

  declare readonly metricNameValue: string;
  declare readonly rawDataValue: string;
  declare readonly smoothedDataValue: string;
  declare readonly defaultViewValue: string;

  declare readonly chartTarget: HTMLElement;
  declare readonly toggleTarget: HTMLButtonElement;
  declare readonly rawLabelTarget: HTMLElement;
  declare readonly smoothedLabelTarget: HTMLElement;

  private rawData: Array<[Date | string, number]> = [];
  private smoothedData: Array<[Date | string, number]> = [];
  private currentView: "raw" | "smoothed" = "smoothed";
  private storageKey: string = "";

  public onInitialize() {
    // Parse JSON data
    try {
      this.rawData = JSON.parse(this.rawDataValue);
      this.smoothedData = JSON.parse(this.smoothedDataValue);
    } catch (error) {
      console.error(
        `[ChartSmoothingToggle] Failed to parse chart data for ${this.metricNameValue}:`,
        error,
      );
      this.rawData = [];
      this.smoothedData = [];
    }

    // Set up localStorage key for persistence
    this.storageKey = `metrics_chart_view_${this.metricNameValue}`;

    // Initialize view from localStorage or default
    this.currentView =
      this.getStoredView() ||
      (this.defaultViewValue as "raw" | "smoothed") ||
      "smoothed";

    return super.onInitialize();
  }

  public connect() {
    super.connect();

    // Apply the initial view
    this.updateChart(false); // Don't animate on initial load
    this.updateToggleState();
  }

  /**
   * Toggle between raw and smoothed views
   */
  public toggle() {
    this.currentView = this.currentView === "raw" ? "smoothed" : "raw";
    this.saveViewPreference();
    this.updateChart(true); // Animate the transition
    this.updateToggleState();
  }

  /**
   * Explicitly set to raw view
   */
  public showRaw() {
    if (this.currentView !== "raw") {
      this.currentView = "raw";
      this.saveViewPreference();
      this.updateChart(true);
      this.updateToggleState();
    }
  }

  /**
   * Explicitly set to smoothed view
   */
  public showSmoothed() {
    if (this.currentView !== "smoothed") {
      this.currentView = "smoothed";
      this.saveViewPreference();
      this.updateChart(true);
      this.updateToggleState();
    }
  }

  /**
   * Update the chart with the current view's data
   */
  private updateChart(animate: boolean = true) {
    const chartElement = this.chartTarget;
    const chartId = chartElement.id;

    if (!chartId) {
      console.warn(
        `[ChartSmoothingToggle] Chart target missing id attribute for ${this.metricNameValue}`,
      );
      return;
    }

    // Get the Chartkick chart instance
    // @ts-ignore - Chartkick is a global
    const chart = window.Chartkick?.charts?.[chartId];

    if (!chart) {
      console.warn(
        `[ChartSmoothingToggle] No Chartkick chart found with id: ${chartId}`,
      );
      return;
    }

    // Select the appropriate dataset
    const newData =
      this.currentView === "raw" ? this.rawData : this.smoothedData;

    // Update the chart with new data
    try {
      chart.updateData(newData);
      console.debug(
        `[ChartSmoothingToggle] Updated chart ${chartId} to ${this.currentView} view`,
      );
    } catch (error) {
      console.error(
        `[ChartSmoothingToggle] Failed to update chart ${chartId}:`,
        error,
      );
    }
  }

  /**
   * Update the visual state of the toggle button
   */
  private updateToggleState() {
    const isSmoothed = this.currentView === "smoothed";

    // Update button ARIA state
    this.toggleTarget.setAttribute("aria-pressed", isSmoothed.toString());

    // Update label highlighting
    if (this.hasRawLabelTarget && this.hasSmoothedLabelTarget) {
      this.updateLabelStyles(this.rawLabelTarget, !isSmoothed);
      this.updateLabelStyles(this.smoothedLabelTarget, isSmoothed);
    }
  }

  /**
   * Update label styling to show active/inactive state
   */
  private updateLabelStyles(labelElement: HTMLElement, isActive: boolean) {
    if (isActive) {
      labelElement.classList.remove("text-gray-500");
      labelElement.classList.add("text-gray-900", "font-semibold");
    } else {
      labelElement.classList.remove("text-gray-900", "font-semibold");
      labelElement.classList.add("text-gray-500");
    }
  }

  /**
   * Get the stored view preference from localStorage
   */
  private getStoredView(): "raw" | "smoothed" | null {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored === "raw" || stored === "smoothed") {
        return stored;
      }
    } catch (error) {
      // localStorage might not be available (privacy mode, etc.)
      console.debug(
        `[ChartSmoothingToggle] Could not access localStorage:`,
        error,
      );
    }
    return null;
  }

  /**
   * Save the current view preference to localStorage
   */
  private saveViewPreference() {
    try {
      localStorage.setItem(this.storageKey, this.currentView);
    } catch (error) {
      // localStorage might not be available (privacy mode, etc.)
      console.debug(
        `[ChartSmoothingToggle] Could not save to localStorage:`,
        error,
      );
    }
  }

  /**
   * Check if raw label target exists
   */
  private get hasRawLabelTarget(): boolean {
    return this.targets.has("rawLabel");
  }

  /**
   * Check if smoothed label target exists
   */
  private get hasSmoothedLabelTarget(): boolean {
    return this.targets.has("smoothedLabel");
  }
}
